import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const openConversation = () => window.dispatchEvent(new CustomEvent("open-conversation"));

const ConversationModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-conversation", handler);
    return () => window.removeEventListener("open-conversation", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Message sent. We will be in touch shortly.");
      setForm({});
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please email info@br-hospitality.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="conversation-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-md flex items-center justify-center px-6"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg bg-ivory text-ink p-10 lg:p-12 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="conversation-modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-5 right-5 text-ink/40 hover:text-ink transition-colors duration-300"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
            <p className="eyebrow mb-3">Start a Conversation</p>
            <h3 className="font-serif text-3xl font-light text-ink mb-8">
              Tell us about your <span className="italic text-bronze">project</span>
            </h3>
            <form data-testid="conversation-form" onSubmit={submit} className="space-y-6">
              <div>
                <label className="field-label" htmlFor="conv-name">Name *</label>
                <input id="conv-name" data-testid="conversation-field-name" type="text" required onChange={set("name")} className="field-input" />
              </div>
              <div>
                <label className="field-label" htmlFor="conv-email">Email *</label>
                <input id="conv-email" data-testid="conversation-field-email" type="email" required onChange={set("email")} className="field-input" />
              </div>
              <div>
                <label className="field-label" htmlFor="conv-company">Company / Property</label>
                <input id="conv-company" data-testid="conversation-field-company" type="text" onChange={set("company")} className="field-input" />
              </div>
              <div>
                <label className="field-label" htmlFor="conv-message">Message *</label>
                <textarea id="conv-message" data-testid="conversation-field-message" rows={4} required onChange={set("message")} className="field-input resize-none" />
              </div>
              <button
                data-testid="conversation-submit-button"
                type="submit"
                disabled={sending}
                className="w-full text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-ink text-ivory hover:bg-bronze hover:text-ink transition-colors duration-500 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConversationModal;
